import { useCallback, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, TextInput } from 'react-native-paper';

import { useWithdrawSuggestion } from '@/features/api/hooks/grapevine-suggestion-events/useWithdrawSuggestion';
import { usePopup } from '@/features/popup/provider/usePopup';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';

const styles = StyleSheet.create({
  container: {
    paddingTop: 8,
    paddingBottom: 32,
    paddingHorizontal: 16,
    gap: 48
  },
  input: {
    minHeight: 180,
    maxHeight: 180,
    backgroundColor: 'transparent'
  }
});

type Props = {
  suggestionId: string;
  onWithdraw: () => void;
};

export const SuggestionWithdrawView: FC<Props> = ({
  suggestionId,
  onWithdraw
}) => {
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'components.suggestionWithdraw'
  });

  const { showToast } = usePopup();

  const { mutate: withdrawSuggestion, isPending } = useWithdrawSuggestion();

  const [text, setText] = useState<string>();

  const send = useCallback(() => {
    if (isEmptyOrNull(text)) return;
    withdrawSuggestion(
      { suggestionId, reason: text },
      {
        onSuccess: () => {
          showToast({
            message: t('toasts.withdraw.success')
          });
          onWithdraw();
        },
        onError: () => showToast({ message: t('toasts.withdraw.error') })
      }
    );
  }, [onWithdraw, showToast, suggestionId, t, text, withdrawSuggestion]);

  return (
    <View style={styles.container}>
      <TextInput
        style={styles.input}
        accessibilityLabel={t('accessibility.editorLabel')}
        accessibilityHint={t('accessibility.editorHint')}
        multiline
        textAlignVertical="top"
        mode="outlined"
        placeholder={t('descriptionHint')}
        label={t('description')}
        value={text}
        onChangeText={setText}
        maxLength={4000}
      />
      <Button
        mode="contained"
        disabled={isEmptyOrNull(text)}
        onPress={send}
        loading={isPending}
      >
        {t('withdraw')}
      </Button>
    </View>
  );
};
